/**
 * How much of a staged passage shows (CB-77).
 *
 * A staged passage's questions each name the lines shown while they are
 * asked, and `./lianxi-yuedu.ts` moves the stage on as they are answered.
 * The stage is written on the passage's body as `data-cb-yuedu-shown`, which
 * Hugo draws with the first question's stage, so the parts of the page drawn
 * later read the same number. The New Words rows are the one such part: a row
 * whose word is first used in a hidden line would give that line away.
 *
 * `yuedu/single.html` marks the later lines `data-cb-yuedu-later`, and
 * `modules/lianxi` hides them from the first paint wherever a script runs.
 * Setting a stage here takes that mark off and hides the lines itself.
 */

/** The attribute the stage is written in, on the passage's body. */
export const stageAttribute = "cbYueduShown";

/** A line or a row, by the line it is or first comes in. */
const lineSelector = "[data-cb-yuedu-line]";
const rowSelector = ".yuedu-word-row[data-cb-yuedu-from]";

/**
 * The stage a body holds, or nothing for a passage shown whole.
 * @param body - The passage's body.
 * @returns How many lines show.
 */
export function stageOf(body: HTMLElement): number | undefined {
  const shown = Number(body.dataset[stageAttribute] ?? "");

  return Number.isInteger(shown) && shown > 0 ? shown : undefined;
}

/**
 * Hold back the New Words rows of words first used past the stage, and give
 * back the rest.
 * @param body - The passage's body, holding the list.
 */
export function holdRows(body: HTMLElement): void {
  const shown = stageOf(body);

  for (const row of body.querySelectorAll<HTMLElement>(rowSelector)) {
    const from = Number(row.dataset["cbYueduFrom"]);

    if (shown !== undefined && from >= shown) {
      row.dataset["cbYueduHeld"] = "";
    } else {
      delete row.dataset["cbYueduHeld"];
    }
  }
}

/**
 * Show a passage's lines up to a stage, or every one of them.
 * @param body - The passage's body.
 * @param shown - How many lines show, or nothing for the whole passage.
 * @param turn - The turn a reply question answers, marked, where one is.
 */
export function showStage(
  body: HTMLElement,
  shown: number | undefined,
  turn?: number,
): void {
  if (shown === undefined) {
    delete body.dataset[stageAttribute];
  } else {
    body.dataset[stageAttribute] = String(shown);
  }

  for (const line of body.querySelectorAll<HTMLElement>(lineSelector)) {
    const at = Number(line.dataset["cbYueduLine"]);

    delete line.dataset["cbYueduLater"];
    line.hidden = shown !== undefined && at >= shown;

    if (at === turn) {
      line.dataset["cbYueduTurn"] = "";
    } else {
      delete line.dataset["cbYueduTurn"];
    }
  }

  holdRows(body);
}
